"use client";
import React, { useState, useEffect, useRef } from 'react';
import { X, Percent, Tag, IndianRupee, Search, CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react';
import { useDiscountType } from '../context/DiscountTypeContext';

const TITLE_WORD_LIMIT = 10;
const DESCRIPTION_WORD_LIMIT = 30;

const getWordCount = (text) =>
  text ? text.trim().split(/\s+/).filter(Boolean).length : 0;

const formatDateForInput = (date) => {
  if (!date) return '';
  if (date.includes('T')) return date.slice(0, 16);
  if (date.includes('-') && date.split('-')[0].length === 4) return `${date}T00:00`;
  const [d, m, y] = date.split('-');
  return `${y}-${m}-${d}T00:00`;
};

const formatDateForAPI = (date) => (date ? date.split('T')[0] : null);

const ItemImage = ({ src, alt, size = 'sm' }) => {
  const [error, setError] = useState(false);
  const dim = size === 'sm' ? 'w-7 h-7' : 'w-8 h-8';

  if (!src || error) {
    return (
      <span
        className={`${dim} rounded-md bg-gradient-to-br from-teal-100 to-teal-50 border border-gray-200 flex items-center justify-center text-[10px] font-bold text-teal-700 flex-shrink-0 select-none`}
      >
        {alt?.[0]?.toUpperCase() || '?'}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      onError={() => setError(true)}
      className={`${dim} rounded-md object-cover border border-gray-200 flex-shrink-0`}
    />
  );
};

const SearchableCheckList = ({
  label,
  items = [],
  selectedIds = [],
  onChange,
  disabled,
  categoryMap,
  selectedCategories = [],
}) => {
  const [query, setQuery] = useState('');

  const isProductPanel = !!categoryMap;

  const visible = items.filter((it) =>
    it.name.toLowerCase().includes(query.toLowerCase())
  );

  const selectedItems = items.filter((it) => selectedIds.includes(it.id));

  const groupedVisible = React.useMemo(() => {
    if (!isProductPanel) return null;

    const groups = [];

    selectedCategories.forEach((cat) => {
      const prods = visible.filter((p) => categoryMap[p.id] === cat.name);
      if (prods.length > 0)
        groups.push({ key: cat.id, label: cat.name, image: cat.image, items: prods });
    });

    const selectedCatNames = new Set(selectedCategories.map((c) => c.name));
    const others = visible.filter((p) => !selectedCatNames.has(categoryMap[p.id]));
    if (others.length > 0)
      groups.push({ key: '__other__', label: 'Other Products', image: null, items: others });

    if (groups.length === 0 && visible.length > 0)
      groups.push({ key: '__all__', label: null, image: null, items: visible });

    return groups;
  }, [visible, selectedCategories, categoryMap, isProductPanel]);

  const groupedSelected = React.useMemo(() => {
    if (!categoryMap) return null;
    const groups = {};
    selectedItems.forEach((it) => {
      const cat = categoryMap[it.id] || 'Uncategorised';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(it);
    });
    return groups;
  }, [selectedItems, categoryMap]);

  const getImageSrc = (item) =>
    item?.image || item?.image_url || item?.thumbnail || item?.product_image || item?.images?.[0] || null;

  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
        <span className="w-2 h-2 bg-teal-500 rounded-full inline-block" />
        {label}
      </label>

      {isProductPanel && selectedCategories.length > 0 && (
        <div className="rounded-xl border border-teal-200 bg-teal-50 px-3 py-2 text-xs text-teal-800">
          <span className="font-semibold">Selected categories: </span>
          <span className="flex flex-wrap gap-1 mt-1">
            {selectedCategories.map((cat) => (
              <span
                key={cat.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100 border border-teal-300 text-teal-800 rounded-full text-[11px] font-medium"
              >
                <ItemImage src={getImageSrc(cat)} alt={cat.name} size="sm" />
                {cat.name}
              </span>
            ))}
          </span>
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${label.toLowerCase()}…`}
          className="w-full pl-9 pr-4 py-2 text-sm bg-white/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 text-gray-700 placeholder-gray-400"
          disabled={disabled}
        />
      </div>

      <div className="max-h-44 overflow-y-auto bg-white/80 border border-gray-200 rounded-xl p-3 space-y-1 scrollbar-thin">
        {visible.length === 0 ? (
          <p className="text-sm text-gray-400 py-1">No results</p>
        ) : isProductPanel && groupedVisible ? (
          groupedVisible.map((group, gi) => (
            <div key={group.key}>
              {gi > 0 && <hr className="border-dashed border-gray-100 my-2" />}

              {group.label && (selectedCategories.length > 0 || group.key === '__other__') && (
                <p className="text-xs font-semibold text-teal-700 mb-1.5 flex items-center gap-1">
                  <ChevronRight className="w-3 h-3 flex-shrink-0" />
                  {group.label}
                  <span className="ml-1 text-[10px] font-normal text-teal-500">
                    ({group.items.length})
                  </span>
                </p>
              )}

              {group.items.map((it) => (
                <label key={it.id} className="flex items-center gap-2 cursor-pointer py-0.5 group pl-1">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(it.id)}
                    onChange={(e) => onChange(it.id, e.target.checked)}
                    className="w-4 h-4 rounded text-teal-600 border-gray-300 focus:ring-teal-500 flex-shrink-0"
                    disabled={disabled}
                  />
                  <ItemImage src={getImageSrc(it)} alt={it.name} size="sm" />
                  <span className="text-sm text-gray-700 group-hover:text-teal-700 transition-colors leading-tight">
                    {it.name}
                  </span>
                </label>
              ))}
            </div>
          ))
        ) : (
          visible.map((it) => (
            <label key={it.id} className="flex items-center gap-2 cursor-pointer py-0.5 group">
              <input
                type="checkbox"
                checked={selectedIds.includes(it.id)}
                onChange={(e) => onChange(it.id, e.target.checked)}
                className="w-4 h-4 rounded text-teal-600 border-gray-300 focus:ring-teal-500 flex-shrink-0"
                disabled={disabled}
              />
              <ItemImage src={getImageSrc(it)} alt={it.name} size="sm" />
              <span className="text-sm text-gray-700 group-hover:text-teal-700 transition-colors leading-tight">
                {it.name}
              </span>
            </label>
          ))
        )}
      </div>

      {selectedItems.length > 0 && (
        <div className="mt-2 border border-gray-100 rounded-xl bg-gray-50 p-3">
          <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
            Selected ({selectedItems.length})
          </p>
          <div className="max-h-44 overflow-y-auto space-y-2 pr-1 scrollbar-thin">
            {groupedSelected ? (
              Object.entries(groupedSelected).map(([cat, prods]) => (
                <div key={cat} className="mb-2">
                  <p className="text-xs font-semibold text-teal-700 mb-1.5 flex items-center gap-1">
                    <ChevronRight className="w-3 h-3 flex-shrink-0" />
                    {cat}
                    <span className="ml-1 text-[10px] font-normal text-teal-500">({prods.length})</span>
                  </p>
                  <div className="flex flex-wrap gap-1.5 pl-4">
                    {prods.map((p) => (
                      <span
                        key={p.id}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-teal-200 text-teal-800 text-xs rounded-full shadow-sm"
                      >
                        <ItemImage src={getImageSrc(p)} alt={p.name} size="xs" />
                        {p.name}
                        <button
                          type="button"
                          onClick={() => onChange(p.id, false)}
                          className="hover:text-red-500 transition-colors ml-0.5"
                          disabled={disabled}
                          aria-label={`Remove ${p.name}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {selectedItems.map((it) => (
                  <span
                    key={it.id}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-teal-200 text-teal-800 text-xs rounded-full shadow-sm"
                  >
                    <ItemImage src={getImageSrc(it)} alt={it.name} size="xs" />
                    {it.name}
                    <button
                      type="button"
                      onClick={() => onChange(it.id, false)}
                      className="hover:text-red-500 transition-colors ml-0.5"
                      disabled={disabled}
                      aria-label={`Remove ${it.name}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const getCatId = (p) =>
  p.category_id ?? p.categoryId ?? p.category ?? p.sub_category_id ?? null;

const OfferModal = ({ isOpen, onClose, offer, products = [], categories = [], onSave }) => {
  const { discountTypes = [] } = useDiscountType();

  const [couponBasis, setCouponBasis] = useState('Product Based');

  const emptyForm = {
    code: '',
    title: '',
    description: '',
    discount_type_id: '',
    value: '',
    minOrderValue: '',
    maxDiscount: '',
    usageLimit: '',
    usageCount: 0,
    userLimit: '',
    startDate: '',
    endDate: '',
    status: 'Active',
    applicableProducts: [],
    applicableCategories: [],
    isFirstTimeUser: false,
    stackable: false,
    qtyThreshold: '',
    qtyDiscountValue: '',
    qtyDiscountType: 'percentage',
    type: '',
    discount_value: '',
    minOrderQty: '',
    manuallyRemovedProducts: [],
  };

  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const modalRef = useRef(null);

  const selectedDiscountType = discountTypes.find(
    (d) => String(d.id) === String(formData.discount_type_id)
  );
  const discountTypeName = selectedDiscountType?.name?.toLowerCase();

  const categoryMap = React.useMemo(() => {
    const map = {};
    (products || []).forEach((p) => {
      const catId = getCatId(p);
      const cat = (categories || []).find((c) => String(c.id) === String(catId));
      map[p.id] = cat?.name || 'Uncategorised';
    });
    return map;
  }, [products, categories]);

  const selectedCategoryObjects = React.useMemo(
    () => (categories || []).filter((c) => formData.applicableCategories.includes(c.id)),
    [categories, formData.applicableCategories]
  );

  const handleCouponBasisChange = (basis) => {
    setCouponBasis(basis);
    if (basis === 'Quantity Based' && !formData.discount_type_id) {
      const firstActive = discountTypes.find((d) => d.status === 'Active');
      if (firstActive) {
        setFormData((prev) => ({ ...prev, discount_type_id: firstActive.id }));
      }
    }
  };

  useEffect(() => {
    if (offer) {
      setCouponBasis(offer.coupon_basis || 'Product Based');

      const loadedProductIds = Array.isArray(offer.product_id)
        ? offer.product_id
        : offer.product_id
          ? String(offer.product_id).split(',').map(Number)
          : [];

      const loadedCategoryIds = Array.isArray(offer.category_id)
        ? offer.category_id
        : offer.category_id
          ? String(offer.category_id).split(',').map(Number)
          : [];

      // ── FIX: Derive manuallyRemovedProducts on load ──
      // Products that belong to selected categories but are absent from
      // the saved product list were intentionally excluded by the user.
      const expectedProductIds = (products || [])
        .filter(
          (p) =>
            p.status === 'Active' &&
            loadedCategoryIds.map(Number).includes(Number(getCatId(p)))
        )
        .map((p) => p.id);

      const derivedManuallyRemoved = expectedProductIds.filter(
        (pid) => !loadedProductIds.includes(pid)
      );

      setFormData({
        code: offer.code || '',
        title: offer.title || '',
        description: offer.description || '',
        discount_type_id: offer.discount_type_id || '',
        value: offer.value || '',
        discount_value: offer.discount_value ?? '',
        minOrderValue: offer.minimum_order_value || '',
        minOrderQty: offer.min_order_qty ?? '',
        maxDiscount: offer.max_discount || '',
        type: offer.type || '',
        usageLimit: offer.total_usage_limit || '',
        userLimit: offer.per_user_limit || '',
        startDate: formatDateForInput(offer.start_date),
        endDate: formatDateForInput(offer.end_date),
        status: offer.status || 'Inactive',
        applicableCategories: loadedCategoryIds,
        applicableProducts: loadedProductIds,
        isFirstTimeUser: offer.user_restrictions === 'first_time_user',
        stackable: Boolean(offer.stackable),
        qtyThreshold: offer.qty_threshold || '',
        qtyDiscountValue: offer.qty_discount_value || '',
        qtyDiscountType: offer.qty_discount_type || 'percentage',
        usageCount: offer.used_count || 0,
        // ── FIX: restored from diff between expected and actual ──
        manuallyRemovedProducts: derivedManuallyRemoved,
      });
    } else {
      setCouponBasis('Product Based');
      setFormData(emptyForm);
    }
    setErrors({});
  }, [offer, isOpen]);
  // NOTE: `products` is intentionally omitted from deps to avoid re-running
  // on every render; if products list is dynamic, add it back carefully.

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape' && isOpen) onClose(); };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      setTimeout(() => modalRef.current?.focus(), 100);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const generateOfferCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
    setFormData((prev) => ({ ...prev, code: result }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'title' && value.trim().split(/\s+/).length > TITLE_WORD_LIMIT) return;
    if (name === 'description' && value.trim().split(/\s+/).length > DESCRIPTION_WORD_LIMIT) return;
    if ((name === 'startDate' || name === 'endDate') && value.split('-')[0]?.length > 4) return;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? (value === '' ? '' : parseFloat(value)) : value,
    }));
  };

  const handleCategoryChange = (catId, checked) => {
    const catProductIds = (products || [])
      .filter((p) => p.status === 'Active' && Number(getCatId(p)) === Number(catId))
      .map((p) => p.id);

    setFormData((prev) => {
      const newCategories = checked
        ? [...prev.applicableCategories, catId]
        : prev.applicableCategories.filter((id) => id !== catId);

      let newProducts = [...prev.applicableProducts];
      let newRemoved = [...prev.manuallyRemovedProducts];

      if (checked) {
        catProductIds.forEach((pid) => {
          // Skip products the user explicitly removed in this session or a previous one
          if (!newProducts.includes(pid) && !newRemoved.includes(pid)) {
            newProducts.push(pid);
          }
        });
      } else {
        const coveredByOther = new Set(
          (products || [])
            .filter(
              (p) =>
                p.status === 'Active' &&
                newCategories.map(Number).includes(Number(getCatId(p)))
            )
            .map((p) => p.id)
        );
        newProducts = newProducts.filter(
          (pid) => !catProductIds.includes(pid) || coveredByOther.has(pid)
        );
        // Clear removed-tracking for this category's products (no longer relevant)
        newRemoved = newRemoved.filter((pid) => !catProductIds.includes(pid));
        return {
          ...prev,
          applicableCategories: newCategories,
          applicableProducts: newProducts,
          manuallyRemovedProducts: newRemoved,
        };
      }

      return { ...prev, applicableCategories: newCategories, applicableProducts: newProducts, manuallyRemovedProducts: newRemoved };
    });
  };

  const handleProductChange = (pid, checked) => {
    setFormData((prev) => {
      const newRemoved = checked
        ? prev.manuallyRemovedProducts.filter((id) => id !== pid) // re-adding clears removal flag
        : [...new Set([...prev.manuallyRemovedProducts, pid])];   // unchecking marks as manually removed

      return {
        ...prev,
        applicableProducts: checked
          ? [...prev.applicableProducts, pid]
          : prev.applicableProducts.filter((id) => id !== pid),
        manuallyRemovedProducts: newRemoved,
      };
    });
  };

  const validateForm = () => {
    const e = {};
    if (!formData.code.trim()) e.code = 'Coupon code is required';
    else if (formData.code.length < 3) e.code = 'Coupon code must be at least 3 characters';
    if (!formData.title.trim()) e.title = 'Coupon title is required';
    if (getWordCount(formData.title) > TITLE_WORD_LIMIT) e.title = `Title cannot exceed ${TITLE_WORD_LIMIT} words`;
    if (getWordCount(formData.description) > DESCRIPTION_WORD_LIMIT) e.description = `Description cannot exceed ${DESCRIPTION_WORD_LIMIT} words`;

    if (couponBasis === 'Product Based') {
      if (!formData.value || formData.value <= 0) e.value = 'Valid discount value is required';
      if (discountTypeName === 'percentage' && formData.value > 100) e.value = 'Percentage discount cannot exceed 100%';
    } else {
      if (!formData.minOrderQty || Number(formData.minOrderQty) <= 0)
        e.minOrderQty = 'Min quantity is required';
      if (!formData.discount_value || Number(formData.discount_value) <= 0)
        e.discount_value = 'Discount value is required';
      if (!formData.discount_type_id)
        e.discount_type_id = 'Discount type is required';
      if (discountTypeName === 'percentage' && Number(formData.discount_value) > 100)
        e.discount_value = 'Percentage cannot exceed 100%';
    }

    if (!formData.startDate) e.startDate = 'Start date is required';
    if (!formData.endDate) e.endDate = 'End date is required';
    if (formData.startDate && formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate))
      e.endDate = 'End date must be after start date';
    if (formData.usageLimit && formData.usageLimit <= 0) e.usageLimit = 'Usage limit must be greater than 0';
    if (formData.userLimit && formData.userLimit <= 0) e.userLimit = 'User limit must be greater than 0';
    if (formData.minOrderValue && formData.minOrderValue < 0) e.minOrderValue = 'Minimum order value cannot be negative';

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);

    try {
      const isQtyBased = couponBasis === 'Quantity Based';

      let discountTypeId = formData.discount_type_id;
      if (!discountTypeId) {
        const firstActive = discountTypes.find((d) => d.status === 'Active');
        discountTypeId = firstActive?.id || null;
      }

      const offerData = {
        id: offer?.id,
        coupon_basis: couponBasis,
        code: formData.code.toUpperCase(),
        title: formData.title,
        description: formData.description || null,
        discount_type_id: discountTypeId ? Number(discountTypeId) : null,
        value: !isQtyBased
          ? (formData.value !== '' ? Number(formData.value) : 0)
          : 0,
        qty_discount_value: isQtyBased
          ? (formData.qtyDiscountValue !== '' ? Number(formData.qtyDiscountValue) : null)
          : null,
        qty_threshold: isQtyBased
          ? (formData.qtyThreshold !== '' ? Number(formData.qtyThreshold) : null)
          : null,
        discount_value: formData.discount_value !== '' && formData.discount_value != null
          ? Number(formData.discount_value)
          : null,
        type: formData.type || null,
        maxDiscount: formData.maxDiscount || null,
        minOrderValue: formData.minOrderValue || null,
        minOrderQty: formData.minOrderQty !== '' && formData.minOrderQty != null
          ? Number(formData.minOrderQty)
          : null,
        usageLimit: formData.usageLimit || null,
        userLimit: formData.userLimit || null,
        startDate: formatDateForAPI(formData.startDate),
        endDate: formatDateForAPI(formData.endDate),
        status: formData.status,
        applicableCategories: formData.applicableCategories,
        applicableProducts: formData.applicableProducts,
        isFirstTimeUser: formData.isFirstTimeUser,
      };

      await onSave(offerData);

    } catch (error) {
      console.error('Error saving coupon:', error);
      setErrors({ general: 'Failed to save Coupon. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const activeCategories = (categories || []).filter((c) => c.status === 'Active');
  const activeProducts = (products || []).filter((p) => p.status === 'Active');

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40 transition-opacity duration-300"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        ref={modalRef}
        className={`fixed top-0 right-0 h-full w-full max-w-4xl bg-white shadow-2xl z-50 transform transition-all duration-300 ease-out flex flex-col overflow-hidden ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}

        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
      >
        {/* Teal header */}
        <div className="flex-shrink-0 bg-teal-700 px-6 py-5 flex items-center justify-between">
          <h2 id="modal-title" className="text-lg font-semibold text-white tracking-tight">
            {offer ? "Edit Coupon" : "Add New Coupon"}
          </h2>
          <button onClick={onClose}
            className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
        <form
          onSubmit={handleSubmit}
          className="px-6 py-6 space-y-6"
        >
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
              {errors.general}
            </div>
          )}

          {/* COUPON BASIS */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
              <span className="w-2 h-2 bg-teal-500 rounded-full inline-block" />
              Coupon Basis *
            </label>
            <div className="flex gap-3">
              {['Product Based', 'Quantity Based'].map((basis) => (
                <button
                  key={basis}
                  type="button"
                  onClick={() => handleCouponBasisChange(basis)}
                  disabled={isLoading}
                  className={`flex-1 py-3 px-5 rounded-xl text-sm font-semibold border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-teal-400 ${
                    couponBasis === basis
                      ? 'bg-teal-600 text-white border-teal-600 shadow-md'
                      : 'bg-white/80 text-gray-600 border-gray-200 hover:border-teal-400 hover:text-teal-600'
                  }`}
                >
                  {basis}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500">
              {couponBasis === 'Product Based'
                ? 'Discount applies based on product or category selection and a discount type.'
                : 'Discount applies when the cart quantity exceeds a set threshold.'}
            </p>
          </div>

          {/* BASIC INFO */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <label htmlFor="code" className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
                <span className="w-2 h-2 bg-teal-500 rounded-full inline-block" />
                Coupon Code *
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  id="code"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  placeholder="Enter Coupon code…"
                  className={`flex-1 px-4 py-3 bg-white/80 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 transition-all text-gray-800 placeholder-gray-500 uppercase ${
                    errors.code ? 'border-red-300' : 'border-gray-200'
                  }`}
                  disabled={isLoading}
                  maxLength={20}
                />
                <button
                  type="button"
                  onClick={generateOfferCode}
                  className="px-4 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400"
                  disabled={isLoading}
                >
                  <Tag className="w-5 h-5" />
                </button>
              </div>
              {errors.code && <p className="text-red-500 text-sm">{errors.code}</p>}
            </div>

            <div className="space-y-3">
              <label htmlFor="title" className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
                <span className="w-2 h-2 bg-teal-500 rounded-full inline-block" />
                Coupon Title *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter Coupon title…"
                className={`w-full px-4 py-3 bg-white/80 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 transition-all text-gray-800 placeholder-gray-500 ${
                  errors.title ? 'border-red-300' : 'border-gray-200'
                }`}
                disabled={isLoading}
                maxLength={100}
              />
              <div className="flex justify-between text-xs mt-1">
                {errors.title && <p className="text-red-500">{errors.title}</p>}
                <span className="text-gray-500 ml-auto">
                  {getWordCount(formData.title)} / {TITLE_WORD_LIMIT} words
                </span>
              </div>
            </div>
          </div>

          {/* DESCRIPTION */}
          <div className="space-y-3">
            <label htmlFor="description" className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
              <span className="w-2 h-2 bg-teal-500 rounded-full inline-block" />
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter Coupon description…"
              rows={3}
              className="w-full px-4 py-3 bg-white/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 transition-all text-gray-800 placeholder-gray-500 resize-none"
              disabled={isLoading}
              maxLength={200}
            />
            <div className="flex justify-end text-xs">
              <span className="text-gray-500">{getWordCount(formData.description)} / {DESCRIPTION_WORD_LIMIT} words</span>
            </div>
          </div>

          {/* PRODUCT BASED: Discount Config */}
          {couponBasis === 'Product Based' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-3">
                  <label htmlFor="discount_type_id" className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
                    <span className="w-2 h-2 bg-teal-500 rounded-full inline-block" />
                    Discount Type *
                  </label>
                  <select
                    id="discount_type_id"
                    name="discount_type_id"
                    value={formData.discount_type_id}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 text-gray-800"
                    disabled={isLoading}
                  >
                    <option value="">Select Discount Type</option>
                    {discountTypes.filter((d) => d.status === 'Active').map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-3">
                  <label htmlFor="value" className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
                    <span className="w-2 h-2 bg-teal-500 rounded-full inline-block" />
                    Discount Value *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      {discountTypeName === 'percentage'
                        ? <Percent className="w-5 h-5 text-gray-400" />
                        : <IndianRupee className="w-5 h-5 text-gray-400" />}
                    </div>
                    <input
                      type="text"
                      inputMode="numeric"
                      id="value"
                      name="value"
                      value={formData.value}
                      onChange={handleInputChange}
                      placeholder={discountTypeName === 'percentage' ? '10' : '100'}
                      className={`w-full pl-10 pr-4 py-3 bg-white/80 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 transition-all text-gray-800 placeholder-gray-500 ${
                        errors.value ? 'border-red-300' : 'border-gray-200'
                      }`}
                      disabled={isLoading}
                    />
                  </div>
                  {errors.value && <p className="text-red-500 text-sm">{errors.value}</p>}
                </div>

                <div className="space-y-3">
                  <label htmlFor="maxDiscount" className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
                    <span className="w-2 h-2 bg-teal-500 rounded-full inline-block" />
                    Max Discount (₹)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    id="maxDiscount"
                    name="maxDiscount"
                    value={formData.maxDiscount}
                    onChange={handleInputChange}
                    placeholder="1000"
                    className="w-full px-4 py-3 bg-white/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 transition-all text-gray-800 placeholder-gray-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <label htmlFor="discount_value" className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
                    <span className="w-2 h-2 bg-teal-500 rounded-full inline-block" />
                    Discount Value (Fixed Override)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <IndianRupee className="w-5 h-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      inputMode="numeric"
                      id="discount_value"
                      name="discount_value"
                      value={formData.discount_value}
                      onChange={handleInputChange}
                      placeholder="e.g. 100"
                      className="w-full pl-10 pr-4 py-3 bg-white/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 transition-all text-gray-800 placeholder-gray-500"
                      disabled={isLoading}
                    />
                  </div>
                  <p className="text-xs text-gray-500">Optional fixed discount amount override</p>
                </div>

                <div className="space-y-3">
                  <label htmlFor="minOrderQty" className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
                    <span className="w-2 h-2 bg-teal-500 rounded-full inline-block" />
                    Min Order Qty
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    id="minOrderQty"
                    name="minOrderQty"
                    value={formData.minOrderQty}
                    onChange={handleInputChange}
                    placeholder="e.g. 5"
                    className="w-full px-4 py-3 bg-white/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 transition-all text-gray-800 placeholder-gray-500"
                    disabled={isLoading}
                  />
                  <p className="text-xs text-gray-500">Minimum item quantity required</p>
                </div>
              </div>
            </div>
          )}

          {/* QUANTITY BASED */}
          {couponBasis === 'Quantity Based' && (
            <div className="bg-teal-50 border border-teal-200 rounded-2xl p-6 space-y-5">
              <h3 className="text-sm font-bold text-teal-800 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Quantity Discount Rules
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label htmlFor="qty_minOrderQty" className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
                    <span className="w-2 h-2 bg-teal-400 rounded-full inline-block" />
                    Min Quantity *
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    id="qty_minOrderQty"
                    name="minOrderQty"
                    value={formData.minOrderQty}
                    onChange={handleInputChange}
                    placeholder="e.g. 10"
                    className={`w-full px-4 py-3 bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 text-gray-800 placeholder-gray-500 ${
                      errors.minOrderQty ? 'border-red-300' : 'border-teal-200'
                    }`}
                    disabled={isLoading}
                  />
                  {errors.minOrderQty && <p className="text-red-500 text-sm">{errors.minOrderQty}</p>}
                  <p className="text-xs text-gray-500">Discount applies when qty &gt; this value</p>
                </div>

                <div className="space-y-2">
                  <label htmlFor="qty_discount_type_id" className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
                    <span className="w-2 h-2 bg-teal-400 rounded-full inline-block" />
                    Discount Type *
                  </label>
                  <select
                    id="qty_discount_type_id"
                    name="discount_type_id"
                    value={formData.discount_type_id}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 text-gray-800 ${
                      errors.discount_type_id ? 'border-red-300' : 'border-teal-200'
                    }`}
                    disabled={isLoading}
                  >
                    <option value="">Select Discount Type</option>
                    {discountTypes.filter((d) => d.status === 'Active').map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                  {errors.discount_type_id && <p className="text-red-500 text-sm">{errors.discount_type_id}</p>}
                </div>

                <div className="space-y-2">
                  <label htmlFor="qty_discount_value" className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
                    <span className="w-2 h-2 bg-teal-400 rounded-full inline-block" />
                    Discount Value *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      {discountTypeName === 'percentage'
                        ? <Percent className="w-5 h-5 text-gray-400" />
                        : <IndianRupee className="w-5 h-5 text-gray-400" />}
                    </div>
                    <input
                      type="text"
                      inputMode="numeric"
                      id="qty_discount_value"
                      name="discount_value"
                      value={formData.discount_value}
                      onChange={handleInputChange}
                      placeholder={discountTypeName === 'percentage' ? '15' : '200'}
                      className={`w-full pl-10 pr-4 py-3 bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 text-gray-800 placeholder-gray-500 ${
                        errors.discount_value ? 'border-red-300' : 'border-teal-200'
                      }`}
                      disabled={isLoading}
                    />
                  </div>
                  {errors.discount_value && <p className="text-red-500 text-sm">{errors.discount_value}</p>}
                </div>
              </div>

              {formData.minOrderQty && formData.discount_value && (
                <div className="bg-teal-100 border border-teal-300 rounded-xl px-4 py-3 text-sm text-teal-800">
                  🎉 When cart has &gt; <strong>{formData.minOrderQty}</strong> items, apply a{' '}
                  <strong>
                    {formData.discount_value}
                    {discountTypeName === 'percentage' ? '%' : '₹'}
                  </strong>{' '}
                  discount.
                </div>
              )}
            </div>
          )}

          {/* ORDER CONSTRAINTS */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <label htmlFor="minOrderValue" className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
                <span className="w-2 h-2 bg-teal-500 rounded-full inline-block" />
                Minimum Order Value (₹)
              </label>
              <input
                type="text"
                inputMode="numeric"
                id="minOrderValue"
                name="minOrderValue"
                value={formData.minOrderValue}
                onChange={handleInputChange}
                placeholder="500"
                className="w-full px-4 py-3 bg-white/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 transition-all text-gray-800 placeholder-gray-500"
                disabled={isLoading}
              />
              {errors.minOrderValue && <p className="text-red-500 text-sm">{errors.minOrderValue}</p>}
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
                <span className="w-2 h-2 bg-teal-500 rounded-full inline-block" />
                User Restrictions
              </label>
              <div className="space-y-3 pt-1">
                <label className="flex items-center cursor-pointer gap-3">
                  <input
                    type="checkbox"
                    name="isFirstTimeUser"
                    checked={formData.isFirstTimeUser}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    disabled={isLoading}
                  />
                  <span className="text-sm text-gray-700">First-time users only</span>
                </label>
                <label className="flex items-center cursor-pointer gap-3">
                  <input
                    type="checkbox"
                    name="stackable"
                    checked={formData.stackable}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    disabled={isLoading}
                  />
                  <span className="text-sm text-gray-700">Can be combined with other Coupons</span>
                </label>
              </div>
            </div>
          </div>

          {/* USAGE LIMITS */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <label htmlFor="usageLimit" className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
                <span className="w-2 h-2 bg-teal-500 rounded-full inline-block" />
                Total Usage Limit
              </label>
              <input
                type="text"
                inputMode="numeric"
                id="usageLimit"
                name="usageLimit"
                value={formData.usageLimit}
                onChange={handleInputChange}
                placeholder="100"
                className="w-full px-4 py-3 bg-white/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 transition-all text-gray-800 placeholder-gray-500"
                disabled={isLoading}
              />
              {errors.usageLimit && <p className="text-red-500 text-sm">{errors.usageLimit}</p>}
              <p className="text-xs text-gray-600">Leave empty for unlimited usage</p>
            </div>

            <div className="space-y-3">
              <label htmlFor="userLimit" className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
                <span className="w-2 h-2 bg-teal-500 rounded-full inline-block" />
                Per User Limit
              </label>
              <input
                type="text"
                inputMode="numeric"
                id="userLimit"
                name="userLimit"
                value={formData.userLimit}
                onChange={handleInputChange}
                placeholder="1"
                className="w-full px-4 py-3 bg-white/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 transition-all text-gray-800 placeholder-gray-500"
                disabled={isLoading}
              />
              {errors.userLimit && <p className="text-red-500 text-sm">{errors.userLimit}</p>}
              <p className="text-xs text-gray-600">Maximum uses per customer</p>
            </div>
          </div>

          {/* DATE RANGE */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <label htmlFor="startDate" className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
                <span className="w-2 h-2 bg-teal-500 rounded-full inline-block" />
                Start Date *
              </label>
              <input
                type="datetime-local"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 bg-white/80 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 transition-all text-gray-800 ${
                  errors.startDate ? 'border-red-300' : 'border-gray-200'
                }`}
                disabled={isLoading}
              />
              {errors.startDate && <p className="text-red-500 text-sm">{errors.startDate}</p>}
            </div>

            <div className="space-y-3">
              <label htmlFor="endDate" className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
                <span className="w-2 h-2 bg-teal-500 rounded-full inline-block" />
                End Date *
              </label>
              <input
                type="datetime-local"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 bg-white/80 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 transition-all text-gray-800 ${
                  errors.endDate ? 'border-red-300' : 'border-gray-200'
                }`}
                disabled={isLoading}
              />
              {errors.endDate && <p className="text-red-500 text-sm">{errors.endDate}</p>}
            </div>
          </div>

          {/* CATEGORIES + PRODUCTS */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SearchableCheckList
              label="Applicable Categories"
              items={activeCategories}
              selectedIds={formData.applicableCategories}
              onChange={handleCategoryChange}
              disabled={isLoading}
            />
            <SearchableCheckList
              label="Applicable Products"
              items={activeProducts}
              selectedIds={formData.applicableProducts}
              onChange={handleProductChange}
              disabled={isLoading}
              categoryMap={categoryMap}
              selectedCategories={selectedCategoryObjects}
            />
          </div>

          {/* STATUS */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
              <span className="w-2 h-2 bg-teal-500 rounded-full inline-block" />
              Status
            </label>
            <div className="flex gap-4">
              {['Active', 'Inactive'].map((s) => (
                <label key={s} className="flex items-center cursor-pointer group">
                  <input
                    type="radio"
                    name="status"
                    value={s}
                    checked={formData.status === s}
                    onChange={handleInputChange}
                    className="sr-only"
                    disabled={isLoading}
                  />
                  <div
                    className={`relative w-6 h-6 rounded-full border-2 transition-all duration-300 ${
                      formData.status === s
                        ? s === 'Active' ? 'border-green-500 bg-green-500' : 'border-gray-500 bg-gray-500'
                        : 'border-gray-300 bg-white group-hover:border-teal-400'
                    }`}
                  >
                    {formData.status === s && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full" />
                      </div>
                    )}
                  </div>
                  <span className="ml-3 text-sm font-medium text-gray-700">{s}</span>
                </label>
              ))}
            </div>
          </div>

          {/* ACTIONS */}
          <div className="flex gap-4 pt-8 pb-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-8 py-4 bg-white/80 text-gray-700 rounded-xl hover:bg-opacity-100 transition-all font-semibold border border-gray-300 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-gray-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-8 py-4 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all font-semibold disabled:opacity-50 flex items-center justify-center gap-2 focus:outline-none"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving…
                </>
              ) : (
                offer ? 'Update Coupon' : 'Create Coupon'
              )}
            </button>
          </div>
        </form>
      </div>
      </div>
    </>
  );
};

export default OfferModal;